/**
 * Wie viel Ton ein PCM-Stück haben muss, bevor es losspielt — je Antwort angepasst. (11.10.2026, „Vorlesen hakt“)
 *
 * ## Der Anlass
 *
 * Martin, 11.10. abends: „Massive Audioausgabe-Probleme beim Vorlesen: es hakt so komisch ab.“ Journal 05.–11.10.,
 * 2.518 PCM-Stücke: 26 mit Aussetzer, und ALLE 26 hatten beim Start nur den Mindest-Vorrat (`vorlauf=` unter 800 ms;
 * 458 solche „kalten“ Stücke, 5,7 % gestört) — von 2.060 Stücken mit mehr Vorrat keines. Soniox und der Server waren dabei
 * gesund (1,2–1,4-fache Echtzeit, erstes Byte 250–620 ms); die Stockung lag zwischen Server und Lautsprecher, und mit
 * 0,5 s Vorrat genügt dort jede Stockung ab einer halben Sekunde. Sie kommen gehäuft (13 von 26 mit einem weiteren
 * gestörten Stück binnen 10 Minuten): eine unruhige Strecke bleibt eine Weile unruhig.
 *
 * ## Die Regel
 *
 *  - Das ERSTE Stück einer Antwort bleibt, wie es war: 0,5 s (`START_VORRAT_S`) — dort zählt die Startlatenz.
 *  - Lief in DIESER Antwort der Vorrat eines Stücks schon einmal leer (Aussetzer ≥ 100 ms), warten die folgenden Stücke auf
 *    1,5 s (`VORRAT_NACH_AUSSETZER_S`).
 *  - Bei zwei Aussetzern oder zusammen 1 s Lücke: 2,5 s (`VORRAT_UNRUHIG_S`).
 *  - Mehr als `VORRAT_FRIST_MS` (tts-pcm.ts) wartet ein Stück auf den größeren Vorrat nie — so wird die Antwort bei einem
 *    langsamen Strom nicht zäh; er spielt dann mit dem, was da ist.
 *
 * ⚠️ Das kostet nur dort Zeit, wo das Stück ohnehin schon spät dran ist: ein WARMES Stück (schon geladen, `gesamtS` beim Aufruf
 *    über dem Vorrat) startet sofort. Ein kaltes Stück wartet bei 1,3-facher Echtzeit rund 0,8 s (1,5 s Vorrat) bis 1,5 s
 *    (2,5 s) länger als bisher — statt mitten im Satz zu haken.
 * ⚠️ Der Rückfall auf den Datei-Weg (tts-pcm-rueckfall.ts, 3 von 50 Stücken) bleibt unverändert der langfristige Schutz.
 */
import { START_VORRAT_S } from "./tts-pcm.js";

/** Nach dem ersten Aussetzer in dieser Antwort: so viel Ton (s) vor dem Start der folgenden Stücke. */
export const VORRAT_NACH_AUSSETZER_S = 1.5;
/** Bei „unruhiger Strecke“ (siehe unten): so viel Ton (s). Obergrenze — länger wartet keine Antwort auf den Anfang eines Stücks. */
export const VORRAT_UNRUHIG_S = 2.5;
/** Unruhig: so viele Aussetzer in dieser Antwort … */
export const VORRAT_UNRUHIG_AUSSETZER = 2;
/** … oder so viele ms Lücke zusammen. */
export const VORRAT_UNRUHIG_LUECKE_MS = 1000;

export class PcmVorrat {
  private aussetzer = 0;
  private lueckeMs = 0;

  /** Ein Stück dieser Antwort ist zu Ende (oder abgebrochen / auf die Datei gewechselt): was seinem Vorrat zugestoßen ist. */
  melde(aussetzer: number, lueckeMs: number): void {
    if (Number.isFinite(aussetzer) && aussetzer > 0) this.aussetzer += aussetzer;
    if (Number.isFinite(lueckeMs) && lueckeMs > 0) this.lueckeMs += lueckeMs;
  }

  /** Läuft die Strecke in dieser Antwort unruhig (mehr als ein Aussetzer, oder eine Lücke von einer Sekunde)? */
  unruhig(): boolean {
    return this.aussetzer >= VORRAT_UNRUHIG_AUSSETZER || this.lueckeMs >= VORRAT_UNRUHIG_LUECKE_MS;
  }

  /** Der Vorrat in Sekunden, den das nächste Stück vor dem Start haben soll. `erstes`: erstes Stück der Antwort. */
  fuer(erstes: boolean): number {
    if (erstes || this.aussetzer === 0) return START_VORRAT_S;
    return this.unruhig() ? VORRAT_UNRUHIG_S : VORRAT_NACH_AUSSETZER_S;
  }

  stand(): Readonly<{ aussetzer: number; lueckeMs: number }> {
    return { aussetzer: this.aussetzer, lueckeMs: this.lueckeMs };
  }
}
